import Dexie, { type Table } from 'dexie';
import { sanitizeCachedCharacter, sanitizeCachedNpc } from './audioDto';
import { type AppEnv, runningEnv } from '../env/appEnv';
import { CATALOG_DB_NAME } from './catalogDb';
import type {
  Campaign,
  Character,
  TableRound,
  WorldElement,
  WorldRelationship,
  WorldTree,
  WorldTreeNode,
} from '../api/types';

export const LOCAL_SCHEMA_VERSION = 2;
// R6-02-c: getrennte Datenbanknamen je Umgebung. Ohne das teilten sich
// Beta-Nutzer 3 und Prod-Nutzer 3 EINE Datenbank samt Schreib-Warteschlange.
// Der Produktions-Praefix bleibt BITGLEICH — ein geaenderter Name wuerde jedem
// echten Nutzer seine Offline-Daten unter den Fuessen wegziehen.
export const LOCAL_DB_PREFIX = 'lorath-local-u';
export const LOCAL_DB_PREFIX_BETA = 'lorath-beta-local-u';

export function localDbPrefixFor(env: AppEnv): string {
  return env === 'beta' ? LOCAL_DB_PREFIX_BETA : LOCAL_DB_PREFIX;
}

export type SyncMetaKey =
  | 'syncCursor'
  | 'lastBootstrapAt'
  | 'bootstrapDone'
  | 'schemaVersion'
  | 'audioDtoVersion'
  | 'assetsDone'
  | 'assetHealDone'
  // W5-E: Katalogversion, mit der die Verweisliste (catalogVisible) erzeugt
  // wurde. Sie geht als ?catalog= ins Delta und entscheidet dort, ob der
  // Server Katalogzeilen ausliefert oder nur Verweise.
  | 'catalogVersion';
export type SyncMetaValue = boolean | number | string | null;

export interface SyncMetaRecord {
  key: SyncMetaKey;
  value: SyncMetaValue;
  updatedAt: string;
}

export type ChatCacheKind = 'campaign' | 'character' | 'table-round' | 'campaign-suggestions';

export interface ChatCacheRecord {
  key: string;
  kind: ChatCacheKind;
  id: number | string;
  messages: unknown[];
  fetchedAt: string;
  // Additive Felder fuer den Antwortvorschlags-Cache (kind === 'campaign-suggestions').
  // Reine Objekt-Erweiterung — der chatCache-Store-Schema-String bleibt unveraendert,
  // daher KEIN LOCAL_SCHEMA_VERSION-Bump noetig.
  suggestions?: unknown[];
  lastMessageId?: number | string;
}

// Asset-Cache-Eintrag: Portrait-Blobs (Beispiel-Kosmos-Bilder etc.) liegen
// offline-faehig in IndexedDB. key = ORIGINAL-Pfad, wie er in element.portrait
// steht (z.B. "/img/example-kosmos/fantasy/xyz.webp") — stabil ueber
// Umgebungen, unabhaengig von VITE_API_BASE.
export interface AssetCacheRecord {
  path: string;
  blob: Blob;
  size: number;
  fetchedAt: string;
}

/**
 * W5-E: Verweis auf eine Zeile des geteilten Vorlagenkatalogs, die DIESES
 * Konto sehen darf. Der Inhalt liegt in der kontouebergreifenden Katalog-DB
 * (catalogDb.ts) — hier steht nur, welche ID sichtbar ist und unter welchem
 * Kosmos DES NUTZERS sie haengt. Alles, was der Nutzer versteckt, geloescht
 * oder durch eine eigene Fassung ersetzt hat (Overlay world_system_hidden +
 * Deduplizierung), fehlt in dieser Liste — der Server rechnet das aus.
 */
export interface CatalogVisibleRecord {
  id: number;
  kosmos_id: number | null;
}

export class LorathLocalDatabase extends Dexie {
  campaigns!: Table<Campaign, number>;
  characters!: Table<Character, number>;
  worldElements!: Table<WorldElement, number>;
  worldRelationships!: Table<WorldRelationship, number>;
  worldTrees!: Table<WorldTree, number>;
  worldTreeNodes!: Table<WorldTreeNode, number>;
  tableRounds!: Table<TableRound, number>;
  meta!: Table<SyncMetaRecord, SyncMetaKey>;
  chatCache!: Table<ChatCacheRecord, string>;
  assetCache!: Table<AssetCacheRecord, string>;
  catalogVisible!: Table<CatalogVisibleRecord, number>;

  constructor(userId: number | string) {
    super(dbNameForUser(userId));

    this.version(LOCAL_SCHEMA_VERSION).stores({
      campaigns: 'id, updated_at',
      characters: 'id, updated_at',
      // kosmos_id-Index ist Voraussetzung dafür, dass das Frontend den aktiven
      // Kosmos OFFLINE filtern kann (Switch-Komfort ohne erneuten Server-Sync).
      // Dexie migriert beim Schema-Bump automatisch + re-indiziert die Spalte.
      worldElements: 'id, updated_at, type, parent_id, kosmos_id',
      worldRelationships: 'id, updated_at, source_type, source_id, target_type, target_id',
      worldTrees: 'id, updated_at',
      worldTreeNodes: 'id, updated_at, tree_id, element_id',
      tableRounds: 'id, updated_at',
      meta: 'key',
      chatCache: 'key, kind, id, fetchedAt',
    });

    // v3: Asset-Cache (Portrait-Blobs) additiv — KEIN Re-Bootstrap der Daten.
    // Wir deklarieren ALLE Stores explizit erneut (identisch zu v2) + assetCache.
    // Dexie merged zwar auch bei Teil-Deklaration, aber die vollständige Liste
    // stellt unmissverständlich sicher, dass KEIN bestehender Store entfernt wird
    // (Review-Absicherung). LOCAL_SCHEMA_VERSION bleibt bewusst 2 (sonst würde
    // die Schema-Heilung einen vollständigen Re-Bootstrap triggern); LSV+1 bleibt
    // dadurch automatisch größer.
    this.version(LOCAL_SCHEMA_VERSION + 1).stores({
      campaigns: 'id, updated_at',
      characters: 'id, updated_at',
      worldElements: 'id, updated_at, type, parent_id, kosmos_id',
      worldRelationships: 'id, updated_at, source_type, source_id, target_type, target_id',
      worldTrees: 'id, updated_at',
      worldTreeNodes: 'id, updated_at, tree_id, element_id',
      tableRounds: 'id, updated_at',
      meta: 'key',
      chatCache: 'key, kind, id, fetchedAt',
      assetCache: 'path, fetchedAt',
    });

    // v4: Verweisliste auf den geteilten Vorlagenkatalog (W5-E) — additiv,
    // nach demselben Muster wie assetCache. LOCAL_SCHEMA_VERSION bleibt
    // bewusst 2: ein Bump wuerde bei JEDEM Nutzer einen vollstaendigen
    // Neu-Bootstrap ausloesen (engine.ts) — genau das soll dieser Umbau
    // vermeiden (Plan 5.4).
    this.version(LOCAL_SCHEMA_VERSION + 2).stores({
      campaigns: 'id, updated_at',
      characters: 'id, updated_at',
      worldElements: 'id, updated_at, type, parent_id, kosmos_id',
      worldRelationships: 'id, updated_at, source_type, source_id, target_type, target_id',
      worldTrees: 'id, updated_at',
      worldTreeNodes: 'id, updated_at, tree_id, element_id',
      tableRounds: 'id, updated_at',
      meta: 'key',
      chatCache: 'key, kind, id, fetchedAt',
      assetCache: 'path, fetchedAt',
      catalogVisible: 'id',
    });

    // v5: Nur alte Audio-DTOs bereinigen; Inhalte, Assets und Cursor bleiben.
    this.version(LOCAL_SCHEMA_VERSION + 3).stores({
      campaigns: 'id, updated_at',
      characters: 'id, updated_at',
      worldElements: 'id, updated_at, type, parent_id, kosmos_id',
      worldRelationships: 'id, updated_at, source_type, source_id, target_type, target_id',
      worldTrees: 'id, updated_at',
      worldTreeNodes: 'id, updated_at, tree_id, element_id',
      tableRounds: 'id, updated_at',
      meta: 'key',
      chatCache: 'key, kind, id, fetchedAt',
      assetCache: 'path, fetchedAt',
      catalogVisible: 'id',
    }).upgrade(async (tx) => {
      await tx.table<Character>('characters').toCollection().modify(row => { Object.assign(row, sanitizeCachedCharacter(row)); delete (row as Character & { call_voice?: unknown }).call_voice; });
      await tx.table<WorldElement>('worldElements').toCollection().modify(row => { Object.assign(row, sanitizeCachedNpc(row)); if (row.type === 'npc') delete (row as WorldElement & { call_voice?: unknown }).call_voice; });
      await tx.table('meta').put({ key: 'audioDtoVersion', value: 0, updatedAt: new Date().toISOString() });
    });
  }
}

const openDatabases = new Map<string, LorathLocalDatabase>();
const LOCAL_DB_CLOSE_CHANNEL = 'lorath-sync-local-db-close-v1';
const DELETE_ATTEMPTS = 3;
const DELETE_TIMEOUT_MS = 2_000;
const RETRY_DELAY_MS = 500;

export function dbNameForUser(userId: number | string, env: AppEnv = runningEnv()): string {
  return `${localDbPrefixFor(env)}${encodeURIComponent(String(userId))}`;
}

export function openForUser(userId: number | string): LorathLocalDatabase {
  const key = String(userId);
  const existing = openDatabases.get(key);
  if (existing) return existing;

  // Warum: getrennte DB-Namen sind die harte Grenze gegen Account-Leaks.
  const db = new LorathLocalDatabase(userId);
  openDatabases.set(key, db);
  return db;
}

export function closeForUser(userId: number | string): void {
  const key = String(userId);
  const db = openDatabases.get(key);
  if (!db) return;
  db.close();
  openDatabases.delete(key);
}

export function closeAllLocalDatabases(): void {
  for (const db of openDatabases.values()) db.close();
  openDatabases.clear();
}

function closeByDatabaseName(name: string): void {
  for (const [key, db] of openDatabases.entries()) {
    if (db.name !== name) continue;
    db.close();
    openDatabases.delete(key);
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

async function getDexieDatabaseNames(): Promise<string[] | null> {
  const dexie = Dexie as typeof Dexie & { getDatabaseNames?: () => Promise<string[]> };
  if (typeof dexie.getDatabaseNames !== 'function') return null;
  try {
    return await dexie.getDatabaseNames();
  } catch {
    return null;
  }
}

async function getIndexedDbDatabaseNames(): Promise<string[] | null> {
  if (typeof indexedDB === 'undefined') return null;
  const indexedDb = indexedDB as IDBFactory & {
    databases?: () => Promise<{ name?: string | null }[]>;
  };
  if (typeof indexedDb.databases !== 'function') return null;
  try {
    const databases = await indexedDb.databases();
    return databases.map((db) => db.name).filter((name): name is string => typeof name === 'string');
  } catch {
    return null;
  }
}

export async function localDatabaseExistsForUser(userId: number | string): Promise<boolean> {
  const names = await getDexieDatabaseNames() ?? await getIndexedDbDatabaseNames();
  if (!names) return false;
  return names.includes(dbNameForUser(userId));
}

async function listLocalDatabaseNames(fallbackUserIds: (number | string)[] = []): Promise<string[]> {
  const names = await getDexieDatabaseNames() ?? await getIndexedDbDatabaseNames() ?? [];
  // Bewusst NUR der Praefix der LAUFENDEN Umgebung: ein Logout in der Beta darf
  // die Produktions-Datenbanken nicht anfassen und umgekehrt.
  const prefix = localDbPrefixFor(runningEnv());
  const localNames = new Set(names.filter((name) => name.startsWith(prefix)));
  for (const userId of fallbackUserIds) localNames.add(dbNameForUser(userId));
  // W5-E: Der geteilte Vorlagenkatalog ist KEINE Nutzerdatenbank und wird beim
  // Abmelden/Kontowechsel NICHT geloescht (Plan 5.4). Der Praefix-Filter oben
  // schuetzt ihn bereits; der explizite Ausschluss haelt die Zusage auch dann,
  // wenn der Name je verwandt aussieht.
  localNames.delete(CATALOG_DB_NAME);
  return [...localNames];
}

function deleteIndexedDbDatabase(name: string): Promise<void> {
  if (typeof indexedDB === 'undefined') return Dexie.delete(name);

  return new Promise((resolve, reject) => {
    let settled = false;
    let blocked = false;
    const request = indexedDB.deleteDatabase(name);
    const timer = globalThis.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(blocked ? 'indexeddb_delete_blocked' : 'indexeddb_delete_timeout'));
    }, DELETE_TIMEOUT_MS);

    const finish = (error?: Error | DOMException | null) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timer);
      if (error) reject(error);
      else resolve();
    };

    request.onblocked = () => {
      blocked = true;
      console.warn(`[Sync] IndexedDB delete blocked for ${name}; waiting for other tabs to close it.`);
    };
    request.onerror = () => finish(request.error ?? new Error('indexeddb_delete_failed'));
    request.onsuccess = () => finish();
  });
}

async function deleteLocalDatabaseName(name: string): Promise<void> {
  closeByDatabaseName(name);
  for (let attempt = 1; attempt <= DELETE_ATTEMPTS; attempt += 1) {
    try {
      await deleteIndexedDbDatabase(name);
      return;
    } catch (error) {
      if (attempt === DELETE_ATTEMPTS) throw error;
      console.warn(`[Sync] Retrying local DB delete for ${name} after ${String((error as Error).message ?? error)}.`);
      await wait(RETRY_DELAY_MS * attempt);
    }
  }
}

export async function closeAndDeleteForUser(userId: number | string): Promise<void> {
  await deleteLocalDatabaseName(dbNameForUser(userId));
}

export async function closeAndDeleteAllLocalDatabases(
  fallbackUserIds: (number | string)[] = [],
): Promise<string[]> {
  closeAllLocalDatabases();
  const names = await listLocalDatabaseNames(fallbackUserIds);
  await Promise.all(names.map((name) => deleteLocalDatabaseName(name)));
  return names;
}

export function broadcastLocalDatabaseClose(): void {
  if (typeof BroadcastChannel !== 'function') return;
  const channel = new BroadcastChannel(LOCAL_DB_CLOSE_CHANNEL);
  channel.postMessage({ type: 'close-local-databases' });
  channel.close();
}

export function subscribeLocalDatabaseClose(callback: () => void): () => void {
  if (typeof BroadcastChannel !== 'function') return () => undefined;
  const channel = new BroadcastChannel(LOCAL_DB_CLOSE_CHANNEL);
  const handleMessage = (event: MessageEvent<unknown>) => {
    const data = event.data as { type?: unknown } | null;
    if (data?.type !== 'close-local-databases') return;
    callback();
  };
  channel.addEventListener('message', handleMessage);
  return () => {
    channel.removeEventListener('message', handleMessage);
    channel.close();
  };
}

export async function getMetaValue<T extends SyncMetaValue>(
  db: LorathLocalDatabase,
  key: SyncMetaKey,
  fallback: T,
): Promise<T> {
  const row = await db.meta.get(key);
  return (row ? row.value : fallback) as T;
}

export async function setMetaValue(
  db: LorathLocalDatabase,
  key: SyncMetaKey,
  value: SyncMetaValue,
): Promise<void> {
  await db.meta.put({ key, value, updatedAt: new Date().toISOString() });
}

export async function markSchemaVersion(db: LorathLocalDatabase): Promise<void> {
  await setMetaValue(db, 'schemaVersion', LOCAL_SCHEMA_VERSION);
}

export function chatCacheKey(kind: ChatCacheKind, id: number | string): string {
  return `${kind}:${id}`;
}

// Cache-Key fuer die Antwortvorschlaege einer Kampagne, gebunden an die letzte
// Erzaehler-Nachricht. So bleiben Vorschlaege offline lesbar und werden nur
// neu geladen, wenn sich die letzte KI-Nachricht aendert.
export function suggestionsCacheKey(campaignId: number | string, lastMessageId: number | string): string {
  return `campaign-suggestions:${campaignId}:${lastMessageId}`;
}

export async function getAllAssetCacheRecords(db: LorathLocalDatabase): Promise<AssetCacheRecord[]> {
  return db.assetCache.toArray();
}

export async function putAssetCacheRecord(
  db: LorathLocalDatabase,
  record: AssetCacheRecord,
): Promise<void> {
  await db.assetCache.put(record);
}

export async function hasAssetCacheRecord(
  db: LorathLocalDatabase,
  path: string,
): Promise<boolean> {
  return (await db.assetCache.where('path').equals(path).count()) > 0;
}
